import type { Action } from 'redux-actions';
import { select, fork, take, put, all } from 'redux-saga/effects';
import i18n from 'i18next';
import ddt from '@wix/da-ddt';
import { reloadStashStream, reloadStashStreamDone } from '../actions';
import { getCurrentFolderId, getCurrentSubfolderId } from '../selectors';
import { uploadFileHandler } from './uploadFile';
import { getAllUploads } from '../../redux/uploads/selectors';
import type { UploadFilePayload } from '../../redux/uploads/types';
import { putErrorNotification } from '../../utils/saga';

const MAX_CONCURRENT_UPLOADS_ALLOWED = 10;

function* uploadFilesSaga(action: Action<UploadFilePayload[]>) {
  // reducer fires before the saga middleware, so the files from the action
  // payload are already in the store
  const allUploads: ReturnType<typeof getAllUploads> = yield select(getAllUploads);
  const activeUploads = allUploads.filter(item => item.hasOwnProperty('progress'));
  const newFileUploads = action.payload;
  const remainingUploadSlots = Math.max(0, MAX_CONCURRENT_UPLOADS_ALLOWED - activeUploads.length);
  if (remainingUploadSlots < newFileUploads.length) {
    ddt.error(
      'MediaManager',
      'Maximum concurrent file uploads exceeded. Truncating...',
      newFileUploads.slice(remainingUploadSlots)
    );
    yield putErrorNotification(
      i18n.t('mediapopup.upload.error.maxConcurrentUploadsExceeded', {
        count: MAX_CONCURRENT_UPLOADS_ALLOWED,
      })
    );
  }
  yield put(reloadStashStream());
  yield take(reloadStashStreamDone); // wait for reload to complete
  const currentFolderId: ReturnType<typeof getCurrentFolderId> = yield select(getCurrentFolderId);
  const currentSubfolderId: ReturnType<typeof getCurrentSubfolderId> =
    yield select(getCurrentSubfolderId);

  yield all(
    newFileUploads.slice(0, remainingUploadSlots).map(payload => {
      const folderId = payload.folderId ?? currentSubfolderId ?? currentFolderId;
      return fork(uploadFileHandler, { ...payload, folderId });
    })
  );
}

export default uploadFilesSaga;
